import type { QuerySpec } from '@helios/powersync-client-core';

// PRD.227 — Billing credit/debit adjustments V1.

export type AdjustmentType = 'debit' | 'credit';

/**
 * Suggested adjustment reasons. Free-text is allowed; these are the
 * chip suggestions surfaced in the entry form so common cases are
 * one-click. Add to this list (rather than replace) as new reasons
 * emerge from clinic usage.
 */
export const DEBIT_REASONS: ReadonlyArray<string> = [
  'Missed charge',
  'Underbilled correction',
  'Post-visit fee',
  'Manual fee correction',
  'Other',
];

export const CREDIT_REASONS: ReadonlyArray<string> = [
  'Returned item',
  'Waived charge',
  'Overcharge correction',
  'Goodwill',
  'Refund',
  'Other',
];

/** PowerSync sqlite row shape for billing_adjustments. */
export interface BillingAdjustmentRow {
  id: string;
  org_id: string | null;
  patient_id: string | null;
  invoice_id: string | null;
  encounter_id: string | null;
  adjustment_type: string | null; // 'debit' | 'credit'
  amount_cents: number | null;
  reason: string | null;
  note: string | null;
  adjusted_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  created_by: string | null;
  updated_by: string | null;
}

// ─── Queries ───

/**
 * All adjustment rows for a patient, newest-first. Drives the history
 * table on Billing Across Visits and feeds patient-balance math.
 */
export function qBillingAdjustmentsByPatient(patientId: string): QuerySpec<[string]> {
  return {
    key: ['billing_adjustments', 'by-patient', patientId] as const,
    sql: `SELECT * FROM billing_adjustments
            WHERE patient_id = ?
            ORDER BY adjusted_at DESC, created_at DESC`,
    params: [patientId],
    watch: ['billing_adjustments'],
  };
}

/**
 * Adjustments tied to a single invoice (newest-first). Used when the
 * invoice detail surface wants to show its own per-invoice adjustment
 * history alongside payments.
 */
export function qBillingAdjustmentsByInvoice(invoiceId: string): QuerySpec<[string]> {
  return {
    key: ['billing_adjustments', 'by-invoice', invoiceId] as const,
    sql: `SELECT * FROM billing_adjustments
            WHERE invoice_id = ?
            ORDER BY adjusted_at DESC, created_at DESC`,
    params: [invoiceId],
    watch: ['billing_adjustments'],
  };
}
